<template>
  <div
    ref="wrapper"
    :class="$style.wrapper"
    :style="{ maxHeight }"
    @scroll.passive="onScroll"
  >
    <table
      id="table"
      :class="$style.table"
    >
      <thead>
        <tr>
          <th
            v-if="selectable"
            :class="$style.checkboxCell"
          >
            <input
              ref="selectAll"
              type="checkbox"
              :checked="checkedAll"
              @change="toggleAll"
            >
          </th>
          <th
            v-for="column in columns"
            :key="column.field"
            :style="{ width: `${column.width}%` }"
            @click="onColumnClick(column.field)"
          >
            {{ column.title }}
            <span
              v-if="sortedColumn === column.field"
              :class="[$style.sort, { [$style.desc]: !isSortAscending }]"
            >
              ▾
            </span>
          </th>
        </tr>
      </thead>
      <tbody
        v-if="tableData.length"
        :class="{ [$style.clickable]: clickable }"
      >
        <tr
          v-if="topSpacerHeight"
          :class="$style.spacer"
          :style="{ height: `${topSpacerHeight}px` }"
          aria-hidden="true"
        >
          <td :colspan="totalColumns" />
        </tr>

        <tr
          v-for="item in visibleRows"
          :key="`${item.row.id}-${item.index}`"
          :data-index="item.index"
          :tabindex="item.index + 1"
          :class="[{ [$style.active]: item.row.id === rowActiveId }, getRowParameterClasses(item.row.id)]"
          @click="onRowClick(item.row.id)"
          @dblclick="onRowDblClick(item.row.id)"
          @keydown.arrow-up.prevent="focusRow(item.index - 1)"
          @keydown.arrow-down.prevent="focusRow(item.index + 1)"
          @focus="onRowClick(item.row.id)"
        >
          <td
            v-if="selectable"
            :class="$style.checkboxCell"
          >
            <input
              type="checkbox"
              :checked="isSelected(item.row.id)"
              @click.stop="toggleRow(item.row.id)"
            >
          </td>
          <td
            v-for="(column, index) in columns"
            :key="column.field"
            :class="{
              [$style.firstColumn]: index === 0,
              [$style.lastColumn]: index === columns.length - 1,
              [$style.textCentr]: column.center,
            }"
            :style="{ width: `${column.width}%` }"
          >
            <slot
              v-if="hasDataColumnSlot(column.field)"
              :name="`column-${column.field}`"
              :row="item.row"
            />
            <template v-else>
              {{ getRowName(item.row[column.field], column.field) }}
            </template>
          </td>
        </tr>

        <tr
          v-if="bottomSpacerHeight"
          :class="$style.spacer"
          :style="{ height: `${bottomSpacerHeight}px` }"
          aria-hidden="true"
        >
          <td :colspan="totalColumns" />
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td :colspan="totalColumns">
            {{ $t('table.noData') }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
  export default {
    model: {
      prop: 'rowActiveId',
      event: 'row-click',
    },
    props: {
      columns: {
        type: Array,
        required: true,
      },
      data: {
        type: Array,
        required: true,
      },
      clickable: {
        type: Boolean,
        default: false,
      },
      rowActiveId: {
        type: [Number, String],
        default: null,
      },
      selectable: {
        type: Boolean,
        default: false,
      },
      // Максимальная высота контейнера таблицы. Внутри появляется свой скролл.
      maxHeight: {
        type: String,
        default: '70vh',
      },
      // Предполагаемая высота одной строки (px). Используется только для расчётов,
      // не навязывается CSS. Если строки визуально разной высоты — увеличьте buffer.
      rowHeight: {
        type: Number,
        default: 33,
      },
      // Кол-во строк, отрисовываемых за пределами вьюпорта сверху и снизу.
      buffer: {
        type: Number,
        default: 5,
      },
      // До какого числа строк рендерим всё без виртуализации.
      virtualThreshold: {
        type: Number,
        default: 50,
      },
    },
    data: () => ({
      sortedColumn: null,
      isSortAscending: true,
      scrollTop: 0,
      viewportHeight: 0,
      resizeObserver: null,
    }),
    computed: {
      totalColumns() {
        return this.columns.length + (this.selectable ? 1 : 0);
      },

      useVirtual() {
        return this.tableData.length > this.virtualThreshold;
      },

      startIndex() {
        if (!this.useVirtual) return 0;

        return Math.max(0, Math.floor(this.scrollTop / this.rowHeight) - this.buffer);
      },

      endIndex() {
        if (!this.useVirtual) return this.tableData.length;

        return Math.min(
          this.tableData.length,
          Math.ceil((this.scrollTop + this.viewportHeight) / this.rowHeight) + this.buffer,
        );
      },

      visibleRows() {
        if (!this.useVirtual) {
          return this.tableData.map((row, index) => ({ row, index }));
        }

        const out = [];

        for (let i = this.startIndex; i < this.endIndex; i++) {
          out.push({ row: this.tableData[i], index: i });
        }

        return out;
      },

      topSpacerHeight() {
        return this.useVirtual ? this.startIndex * this.rowHeight : 0;
      },

      bottomSpacerHeight() {
        return this.useVirtual ? (this.tableData.length - this.endIndex) * this.rowHeight : 0;
      },

      checkedAll() {
        return this.tableData.length > 0 && this.selectedRows.length === this.tableData.length;
      },
      isIndeterminate() {
        return this.selectedRows.length > 0 && this.selectedRows.length < this.tableData.length;
      },
      selectedRows() {
        return this.$store.getters['logs/downloadData/getSelectedRows'];
      },
      dataParameters() {
        return this.data.reduce((acc, item) => {
          acc[item.id] = {
            color: {
              1: 'yellow',
              2: 'red',
            }[item.statusDangerous],
          };

          return acc;
        }, {});
      },
      tableData() {
        const { sortedColumn, isSortAscending } = this;

        const sortedColumnObject = this.columns.find((i) => i.field === sortedColumn);

        if (
          sortedColumnObject &&
          this.hasDataColumnSlot(sortedColumn) &&
          typeof sortedColumnObject.sortFn === 'function'
        ) {
          const data = [...this.data].sort(sortedColumnObject.sortFn(isSortAscending));

          this.$store.commit('logs/printData/setData', data);
          return data;
        }

        const data = [...this.data].sort(this.compare);
        this.$store.commit('logs/printData/setData', data);

        return data;
      },
    },
    watch: {
      isIndeterminate(value) {
        this.$nextTick(() => {
          if (this.$refs.selectAll) {
            this.$refs.selectAll.indeterminate = value;
          }
        });
      },

      // При смене сортировки возвращаемся наверх, иначе пользователь «зависает»
      // в середине новых данных.
      sortedColumn() {
        this.resetScroll();
      },
      isSortAscending() {
        this.resetScroll();
      },

      // Если данные укоротились — не даём scrollTop «улететь» за пределы.
      'tableData.length': function () {
        this.$nextTick(this.clampScroll);
      },
    },
    created() {
      const currentSorted = this.columns.find((item) => item?.sorted);
      if (currentSorted) this.sortedColumn = currentSorted.field;
      this.$store.commit('logs/downloadData/clearSelected');
    },
    mounted() {
      this.$nextTick(() => {
        if (this.$refs.wrapper) {
          this.viewportHeight = this.$refs.wrapper.clientHeight;
        }
        this.setupResizeObserver();
      });
    },
    beforeDestroy() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    methods: {
      setupResizeObserver() {
        if (!window.ResizeObserver || !this.$refs.wrapper) return;

        this.resizeObserver = new ResizeObserver(([entry]) => {
          if (entry) {
            this.viewportHeight = entry.contentRect.height;
          }
        });

        this.resizeObserver.observe(this.$refs.wrapper);
      },

      onScroll(e) {
        this.scrollTop = e.target.scrollTop;
      },

      resetScroll() {
        if (!this.$refs.wrapper) return;

        this.$refs.wrapper.scrollTop = 0;
        this.scrollTop = 0;
      },

      clampScroll() {
        const { wrapper } = this.$refs;
        if (!wrapper) return;

        const max = wrapper.scrollHeight - wrapper.clientHeight;

        if (wrapper.scrollTop > max) {
          wrapper.scrollTop = Math.max(0, max);
          this.scrollTop = wrapper.scrollTop;
        }
      },

      isSelected(id) {
        return this.selectedRows.includes(id);
      },
      toggleRow(id) {
        this.$store.commit('logs/downloadData/toggleRow', id);
      },

      toggleAll(e) {
        const { checked } = e.target;

        const rows = checked ? this.tableData.map((i) => i.id) : [];

        this.$store.commit('logs/downloadData/setSelectedAll', rows);
      },
      getRowName(name, field) {
        const isDbString = field === 'category' && typeof name === 'string' && name.startsWith('$');
        return isDbString ? this.$t(`db.${name}`) : name;
      },
      compare(a, b) {
        a = a[this.sortedColumn];
        b = b[this.sortedColumn];

        if (typeof a === 'string' && typeof b === 'string') {
          a = a.toLowerCase();
          b = b.toLowerCase();
        }

        if (this.isSortAscending) {
          if (a > b) return -1;
          if (a < b) return 1;
          return 0;
        }

        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
      },
      hasDataColumnSlot(field) {
        return Object.prototype.hasOwnProperty.call(this.$scopedSlots, `column-${field}`);
      },
      isSortedColumn(column) {
        return this.sortedColumn === column;
      },
      onColumnClick(column) {
        if (column === this.sortedColumn) {
          this.isSortAscending = !this.isSortAscending;
        } else {
          this.sortedColumn = column;
          this.isSortAscending = true;
        }
      },
      onRowClick(id) {
        if (this.clickable && this.rowActiveId !== id) {
          // Второе условие - костыль
          this.$emit('row-click', id);
        }
      },
      onRowDblClick(id) {
        if (this.clickable) {
          this.$emit('row-dblclick', id);
        }
      },

      // С arrow-навигацией по строкам в виртуальном списке нужно сначала
      // подскроллить контейнер так, чтобы целевая строка попала в окно
      // рендера, дождаться перерисовки и только потом фокусировать.
      async focusRow(idx) {
        if (idx < 0 || idx >= this.tableData.length) return;

        const { wrapper } = this.$refs;
        if (!wrapper) return;

        if (this.useVirtual) {
          const rowTop = idx * this.rowHeight;
          const rowBottom = rowTop + this.rowHeight;
          const vTop = wrapper.scrollTop;
          const vBottom = vTop + wrapper.clientHeight;

          if (rowTop < vTop) {
            wrapper.scrollTop = rowTop;
          } else if (rowBottom > vBottom) {
            wrapper.scrollTop = rowBottom - wrapper.clientHeight;
          }

          this.scrollTop = wrapper.scrollTop;
          await this.$nextTick();
        }

        const rowEl = wrapper.querySelector(`tr[data-index="${idx}"]`);
        if (rowEl) rowEl.focus();
      },

      getRowParameterClasses(rowId) {
        const row = this.dataParameters[rowId];

        if (!row) {
          return [];
        }
        const classes = [];

        // eslint-disable-next-line default-case
        switch (row.color) {
          case 'yellow':
            classes.push(this.$style.colorYellow);
            break;
          case 'red':
            classes.push(this.$style.colorRed);
            break;
        }

        return classes;
      },
    },
  };
</script>

<style lang="scss" module>
  .wrapper {
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin !important;
  }

  .table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;

    td,
    th {
      border: 1px solid $borderColor;
      padding: 6px;
      word-break: break-all;
    }

    thead th {
      position: sticky;
      top: 0;
      z-index: 2;
      background: #dbf1ff;
    }

    .firstColumn {
      padding-left: 20px;
    }

    .lastColumn {
      padding-right: 20px;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }

    th {
      user-select: none;
      background: $light-gray;
      font: 400 13px/18px Inter, sans-serif;
      cursor: pointer;
      background: #dbf1ff;
    }

    td {
      font-size: 12px;
    }

    tbody.clickable tr {
      cursor: pointer;

      &:hover {
        background: $light-gray;
      }

      &.active {
        background: $light-gray;
      }
    }

    tr.colorYellow {
      background-color: $light-yellow;

      &:hover {
        background: $yellow !important;
      }

      &.active {
        background: $yellow !important;
      }
    }

    tr.colorRed {
      background-color: $light-red;

      &:hover {
        background: $red !important;
      }

      &.active {
        background: $red !important;
      }
    }

    .spacer td {
      border: none !important;
      padding: 0 !important;
      background: transparent !important;
      line-height: 0;
    }
  }

  .sort {
    display: inline-block;
    margin-left: 6px;
    color: $dark-gray;
  }

  .desc {
    transform: rotate(180deg);
  }

  .textCentr {
    text-align: center;
  }

  .checkboxCell {
    width: 40px;
    text-align: center;
    vertical-align: middle;

    input[type='checkbox'] {
      margin: 0;
    }
  }
</style>
